import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredManga, popularMangaList, reviews } from '@/mocks/manga';
import type { MangaItem } from '@/mocks/manga';

function DetailSchemaOrg({ manga }: { manga: MangaItem }) {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${manga.title} - 在线免费阅读 - 古古漫画网`,
        description: manga.description,
        author: { '@type': 'Person', name: manga.author },
        publisher: { '@type': 'Organization', name: '古古漫画网' },
        datePublished: '2024-01-01',
        dateModified: manga.latestChapter.updateDate,
        url: `http://sylbdj.com/manga/${manga.id}`,
      },
      {
        '@type': 'WebPage',
        name: `${manga.title} - 古古漫画网`,
        url: `http://sylbdj.com/manga/${manga.id}`,
        description: `${manga.title}漫画在线免费阅读，${manga.description.substring(0, 100)}`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://sylbdj.com/' },
            { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'http://sylbdj.com/category' },
            { '@type': 'ListItem', position: 3, name: manga.title, item: `http://sylbdj.com/manga/${manga.id}` },
          ],
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `${manga.title}可以在线免费阅读吗？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `是的，古古漫画网提供${manga.title}漫画的在线免费阅读服务，无需注册即可畅享全部章节。`,
            },
          },
          {
            '@type': 'Question',
            name: `${manga.title}多久更新一次？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `${manga.title}目前状态为${manga.status}，最新章节于${manga.latestChapter.updateDate}更新，我们会第一时间同步最新内容。`,
            },
          },
          {
            '@type': 'Question',
            name: `如何收藏${manga.title}的更新？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: '您可以在古古漫画网订阅邮件通知，或收藏本页面，每次访问即可看到最新章节更新信息。',
            },
          },
        ],
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

const userReviews = reviews;

export default function MangaDetailPage() {
  const { id } = useParams<{ id: string }>();

  const manga =
    id === 'shuumatsu-no-valkyrie'
      ? featuredManga
      : popularMangaList.find((m) => m.id === id) || featuredManga;

  const relatedManga = popularMangaList.filter((m) => m.id !== manga.id).slice(0, 3);

  return (
    <>
      <DetailSchemaOrg manga={manga} />
      <Navbar />

      <main>
        {/* CTA Hero Section */}
        <section
          className="relative w-full min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden"
          aria-label="漫画详情入口"
        >
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Dramatic%20manga%20panorama%20illustration%20of%20epic%20god%20versus%20human%20battle%20scene%2C%20wide%20composition%20with%20cinematic%20lighting%2C%20warm%20crimson%20and%20deep%20gold%20tones%2C%20Japanese%20comic%20art%20style%20with%20sweeping%20action%20effects%2C%20dark%20atmospheric%20sky%20with%20dramatic%20clouds%2C%20stylized%20artistic%20rendering%2C%20professional%20quality%20for%20chapter%20splash%20page&width=1600&height=600&seq=detail-hero-valkyrie&orientation=landscape"
              alt={`${manga.title}漫画阅读`}
              title={`${manga.title}在线免费阅读`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/50" />

          <div className="relative z-10 w-full px-4 md:px-6 py-20">
            <div className="max-w-4xl">
              <span className="whitespace-nowrap px-3 py-1 bg-primary-500/20 text-primary-200 text-xs rounded-full border border-primary-400/30">
                {manga.status}
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mt-4 leading-tight">
                {manga.title}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {manga.tags.map((tag) => (
                  <span key={tag} className="whitespace-nowrap px-2 py-0.5 bg-white/10 text-white/70 text-xs rounded">
                    {tag}
                  </span>
                ))}
              </div>
              <p className="text-sm md:text-base text-white/60 mt-6 max-w-xl leading-relaxed">
                {manga.description}
              </p>
              <div className="flex items-center gap-6 mt-6">
                <span className="text-sm text-white/70">
                  <i className="ri-star-fill text-accent-400 mr-1" />
                  {manga.rating}
                </span>
                <span className="text-sm text-white/70">
                  <i className="ri-eye-line mr-1" />
                  {manga.views}
                </span>
                <span className="text-sm text-white/70">
                  <i className="ri-user-line mr-1" />
                  {manga.author}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 py-3">
            <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <span>/</span>
              <Link to="/category" className="hover:text-primary-500 transition-colors cursor-pointer">漫画分类</Link>
              <span>/</span>
              <span className="text-foreground-800">{manga.title}</span>
            </nav>
          </div>
        </div>

        {/* Chapter List */}
        <section className="bg-background-50 py-10 md:py-14" aria-label="章节列表">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                章节列表
                <span className="text-sm font-normal text-foreground-500 ml-2">
                  (共{manga.chapters.length}话)
                </span>
              </h2>
              <div className="flex gap-2">
                <button className="whitespace-nowrap px-4 py-1.5 bg-secondary-100 text-secondary-700 text-xs rounded-md hover:bg-secondary-200 transition-colors cursor-pointer">
                  正序
                </button>
                <button className="whitespace-nowrap px-4 py-1.5 bg-primary-100 text-primary-700 text-xs rounded-md hover:bg-primary-200 transition-colors cursor-pointer">
                  倒序
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {manga.chapters.slice(0, 30).map((chapter) => (
                <button
                  key={chapter.id}
                  className="text-left p-4 bg-background-50 border border-background-200 rounded-lg hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer group"
                >
                  <span className="font-heading text-sm font-semibold text-foreground-800 group-hover:text-primary-600">
                    {chapter.title}
                  </span>
                  <p className="text-xs text-foreground-400 mt-1">{chapter.updateDate}</p>
                </button>
              ))}
            </div>

            {manga.chapters.length > 30 && (
              <div className="text-center mt-8">
                <button className="whitespace-nowrap px-6 py-2 border border-background-300 text-sm text-foreground-600 rounded-md hover:bg-background-100 transition-colors cursor-pointer">
                  查看更多章节
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Related Manga */}
        <section className="bg-background-100 py-10 md:py-14" aria-label="相关推荐">
          <div className="w-full px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-8">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop="">
              {relatedManga.map((item) => (
                <Link
                  key={item.id}
                  to={`/manga/${item.id}`}
                  className="group cursor-pointer rounded-lg overflow-hidden relative aspect-[3/4]"
                >
                  <img
                    src={item.coverImage}
                    alt={`${item.title}漫画`}
                    title={`${item.title}漫画在线免费阅读 - 古古漫画网`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-5">
                    <h3 className="font-heading text-lg font-bold text-white">
                      {item.title}
                    </h3>
                    <p className="text-xs text-white/70 mt-1">{item.author}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Reader Comments */}
        <section className="bg-background-50 py-10 md:py-14" aria-label="读者评论">
          <div className="w-full px-4 md:px-6">
            <div className="mb-10">
              <span className="whitespace-nowrap px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full">
                社区热评
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-3">
                读者的真实声音
              </h2>
            </div>

            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5">
              {userReviews.map((review, idx) => (
                <div
                  key={review.id}
                  className="break-inside-avoid mb-5 bg-background-50 border border-background-200 rounded-lg p-5 hover:border-background-300 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-secondary-200">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.username}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 mt-4 leading-relaxed">
                    &ldquo;{review.comment}&rdquo;
                  </p>
                  <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}